import { Link } from "react-router-dom";
import { BookOpen, Clock, GraduationCap, Heart, Info, PlayCircle, Star, Users } from "lucide-react";
import { formatPrice } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { useBuyFormation } from "@/hooks/useFormations";

const LEVEL_TONE = {
  "Débutant": "bg-emerald-500",
  "Intermédiaire": "bg-brand",
  "Avancé": "bg-rose-500",
  "Tous niveaux": "bg-violet-500",
};

export function FormationCard({ formation, index = 0 }) {
  const { currency } = useStore();
  const buy = useBuyFormation();
  const hasAccess = formation.has_access;

  return (
    <article
      data-testid={`formation-card-${formation.slug}`}
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
      className="group flex animate-fade-up flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-[0_8px_30px_rgba(0,0,0,0.05)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-12px_rgba(0,0,0,0.18)]"
    >
      <Link to={`/formations/${formation.slug}`} className="relative block overflow-hidden">
        <img
          src={formation.image_url}
          alt={formation.title}
          loading={index > 3 ? "lazy" : "eager"}
          className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
        <span
          className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-white ${LEVEL_TONE[formation.level] || "bg-slate-600"}`}
        >
          {formation.level}
        </span>
        {formation.duration && (
          <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur">
            <Clock className="h-3 w-3" /> {formation.duration}
          </span>
        )}
        <span className="absolute bottom-3 left-3 flex h-11 w-11 items-center justify-center rounded-xl bg-white/95 shadow-lg backdrop-blur">
          <GraduationCap className="h-6 w-6 text-brand" />
        </span>
        {formation.discount_percent > 0 && (
          <span className="absolute bottom-3 right-3 rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-black text-white">
            -{formation.discount_percent}%
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <Link to={`/formations/${formation.slug}`}>
          <h3 className="line-clamp-2 text-[15px] font-extrabold leading-snug text-ink transition-colors duration-200 group-hover:text-brand">
            {formation.title}
          </h3>
        </Link>
        <p className="mt-1.5 line-clamp-2 text-[13px] leading-snug text-slate-500">{formation.short_description}</p>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] font-semibold text-slate-500">
          <span className="flex items-center gap-1">
            <BookOpen className="h-3.5 w-3.5" /> {formation.lessons_count} cours
          </span>
          {formation.students_count > 0 && (
            <span className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" /> {formation.students_count.toLocaleString("fr-FR")}
            </span>
          )}
          {formation.reviews_count > 0 && (
            <span className="flex items-center gap-1 text-amber-500">
              <Star className="h-3.5 w-3.5" fill="currentColor" />
              <span className="font-bold text-ink">{formation.rating}</span>
              <span className="text-slate-400">({formation.reviews_count})</span>
            </span>
          )}
        </div>

        <div className="mt-auto pt-4">
          {hasAccess ? (
            <div className="flex flex-col gap-2">
              {formation.progress > 0 && (
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${formation.progress}%` }} />
                </div>
              )}
              <Link
                to={`/mes-formations/${formation.id}`}
                data-testid={`formation-access-${formation.slug}`}
                className="flex w-full items-center justify-center gap-1.5 rounded-full bg-emerald-600 px-3 py-2.5 text-[13.5px] font-bold text-white transition-[background-color,transform] duration-200 hover:bg-emerald-700 active:scale-95"
              >
                <GraduationCap className="h-4 w-4" /> Accéder à la formation
              </Link>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-display text-lg font-extrabold text-ink" data-testid={`formation-price-${formation.slug}`}>
                  {formatPrice(formation.price, currency)}
                </span>
                {formation.old_price > formation.price && (
                  <span className="text-xs text-slate-400 line-through">
                    {formatPrice(formation.old_price, currency)}
                  </span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Link
                  to={`/formations/${formation.slug}`}
                  data-testid={`formation-info-${formation.slug}`}
                  className="flex items-center justify-center gap-1.5 rounded-full border border-line px-3 py-2.5 text-[12.5px] font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
                >
                  <Info className="h-4 w-4" /> Plus d'infos
                </Link>
                <button
                  data-testid={`formation-buy-${formation.slug}`}
                  onClick={() => buy(formation)}
                  className="flex items-center justify-center gap-1.5 rounded-full bg-brand px-3 py-2.5 text-[12.5px] font-bold text-white transition-[background-color,transform] duration-200 hover:bg-brand-dark active:scale-95"
                >
                  <PlayCircle className="h-4 w-4" /> Acheter
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

export function FormationCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-white">
      <div className="aspect-[16/10] animate-pulse bg-slate-100" />
      <div className="space-y-2 p-4">
        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
        <div className="h-9 w-full animate-pulse rounded-full bg-slate-100" />
      </div>
    </div>
  );
}

export default FormationCard;
